import { useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight, ArrowLeft, MapPin, Zap, Star } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";

const STEPS = [
  { key: "family_size", title: "Who's riding with you?", type: "choice", options: [
    { v: 1, l: "Just me" }, { v: 2, l: "2 people" }, { v: 4, l: "Family of 4" }, { v: 6, l: "5+" },
  ]},
  { key: "use_case", title: "How will you use it most?", type: "choice", options: [
    { v: "daily commute", l: "Daily commute" },
    { v: "road trips", l: "Road trips" },
    { v: "weekend driver", l: "Weekend driver" },
    { v: "business", l: "Business / clients" },
    { v: "off-road", l: "Off-road / adventure" },
  ]},
  { key: "body_pref", title: "Body style preference?", type: "choice", options: [
    { v: "any", l: "Open to anything" },
    { v: "sedan", l: "Sedan" },
    { v: "suv", l: "SUV" },
    { v: "truck", l: "Truck" },
    { v: "coupe", l: "Coupe / Sport" },
    { v: "ev", l: "Electric" },
  ]},
  { key: "luxury_pref", title: "Vibe?", type: "choice", options: [
    { v: "luxury", l: "Luxury — name brand, no compromises" },
    { v: "premium", l: "Premium — quality, but value matters" },
    { v: "practical", l: "Practical — get the job done" },
  ]},
  { key: "budget_monthly", title: "Monthly payment comfort zone?", type: "slider", min: 300, max: 2000, step: 50, suffix: "/mo" },
  { key: "needs_awd", title: "Need AWD?", type: "toggle" },
  { key: "towing", title: "Towing requirement?", type: "toggle" },
];

export default function Matchmaker() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    family_size: 2, use_case: "daily commute", body_pref: "any",
    luxury_pref: "premium", budget_monthly: 800, needs_awd: false, towing: false,
    commute_miles: 30, notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const current = STEPS[step];

  const setAns = (k, v) => setAnswers({ ...answers, [k]: v });

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/matchmaker", answers);
      setResult(data);
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12" data-testid="matchmaker-result">
        <p className="label-mono mb-3">Your Personalized Matches</p>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter mb-3">Curated for you.</h1>
        <p className="text-gray-300 max-w-2xl mb-10 italic">{result.summary}</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {result.recommendations.map((d, i) => (
            <Link to={`/lease-deals/${d.id}`} key={d.id} data-testid={`match-rec-${i}`} className="group bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden hover-lift">
              <div className="aspect-[16/10] overflow-hidden bg-[#12121A] relative">
                <img src={d.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                <div className="absolute top-3 left-3 px-2 py-1 rounded-full silver-gradient text-[#050507] text-[10px] font-black tracking-widest">FIT {d.fit_score}</div>
              </div>
              <div className="p-5">
                <div className="label-mono">{d.brand} · {d.year}</div>
                <div className="font-display text-xl mt-1">{d.model}</div>
                <div className="font-display text-2xl mt-3">${Math.round(d.monthly_payment)}/mo</div>
                <p className="text-xs text-gray-400 mt-3 leading-relaxed">{d.why}</p>
              </div>
            </Link>
          ))}
        </div>
        <button onClick={() => { setResult(null); setStep(0); }} className="mt-10 px-6 py-3 rounded-full border border-white/20 hover:bg-white/5 text-sm" data-testid="matchmaker-restart">
          Start Over
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-5 lg:px-10 py-16 md:py-20" data-testid="matchmaker-page">
      <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-silver" /><span className="label-mono">AI Matchmaker</span></div>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-3">Netflix for cars.</h1>
      <p className="text-gray-400 mb-10">Answer 7 quick questions. We'll match you to the top vehicles in our marketplace — ranked by fit.</p>

      <div className="glass rounded-2xl p-8 md:p-10">
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((_, i) => (
            <div key={i} className={`flex-1 h-1 rounded-full ${i <= step ? "silver-gradient" : "bg-white/10"}`} />
          ))}
        </div>
        <div className="label-mono mb-3">Step {Math.min(step + 1, STEPS.length)} of {STEPS.length}</div>

        {step < STEPS.length && (
          <div className="animate-fade-up">
            <h2 className="font-display text-2xl md:text-3xl mb-7">{current.title}</h2>
            {current.type === "choice" && (
              <div className="grid sm:grid-cols-2 gap-2">
                {current.options.map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => { setAns(current.key, opt.v); setTimeout(next, 200); }}
                    data-testid={`matchmaker-opt-${current.key}-${opt.v}`}
                    className={`p-4 rounded-xl border text-left text-sm transition ${answers[current.key] === opt.v ? "silver-gradient text-[#050507] border-transparent" : "border-white/10 hover:border-white/30 hover:bg-white/5"}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            )}
            {current.type === "slider" && (
              <div>
                <div className="text-center font-display text-5xl font-light mb-7">${answers[current.key].toLocaleString()}{current.suffix}</div>
                <Slider min={current.min} max={current.max} step={current.step} value={[answers[current.key]]} onValueChange={(v) => setAns(current.key, v[0])} data-testid={`matchmaker-slider-${current.key}`} />
              </div>
            )}
            {current.type === "toggle" && (
              <div className="flex gap-3">
                <button onClick={() => { setAns(current.key, true); setTimeout(next, 200); }} className={`flex-1 py-4 rounded-xl border ${answers[current.key] === true ? "silver-gradient text-[#050507] border-transparent" : "border-white/10"}`} data-testid={`matchmaker-yes-${current.key}`}>Yes</button>
                <button onClick={() => { setAns(current.key, false); setTimeout(next, 200); }} className={`flex-1 py-4 rounded-xl border ${answers[current.key] === false ? "silver-gradient text-[#050507] border-transparent" : "border-white/10"}`} data-testid={`matchmaker-no-${current.key}`}>No</button>
              </div>
            )}
          </div>
        )}

        {step >= STEPS.length && (
          <div className="text-center py-6">
            <h2 className="font-display text-2xl mb-5">Ready when you are.</h2>
            <button onClick={submit} disabled={submitting} className="px-10 py-4 rounded-full silver-gradient text-[#050507] font-bold tracking-wide" data-testid="matchmaker-submit">
              {submitting ? "Matching…" : "Show My Matches"}
            </button>
          </div>
        )}

        <div className="flex justify-between mt-8">
          <button onClick={back} disabled={step === 0} className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white disabled:opacity-30" data-testid="matchmaker-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length && current.type !== "choice" && current.type !== "toggle" && (
            <button onClick={next} className="inline-flex items-center gap-2 text-sm text-white" data-testid="matchmaker-next">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
